/**
 * Product media gallery.
 *
 * Only "thumbnail"/"thumbnail_slider" gallery layouts (single media item shown at a
 * time) run as Swiper carousels — see product-media-gallery.liquid for why. "stacked"
 * and "columns" show every media item at once and keep Dawn's original
 * slider-component-based behavior untouched.
 *
 * setActiveMedia(mediaId, prepend) is kept as the public API either way, because
 * product-info.js calls it after a variant change (and mutates the viewer's <ul>
 * right before).
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.useSwiperGallery = this.dataset.swiperGallery === 'true';

        if (!this.elements.viewer) return;

        if (this.useSwiperGallery) {
          this.initSwipers();
        } else {
          this.initNativeSlider();
        }
      }

      disconnectedCallback() {
        this.viewerSwiper?.destroy(true, true);
        this.thumbsSwiper?.destroy(true, true);
        this.viewerSwiper = null;
        this.thumbsSwiper = null;
        this.initialised = false;
      }

      setActiveMedia(mediaId, prepend) {
        if (this.useSwiperGallery) {
          this.setActiveMediaSwiper(mediaId, prepend);
        } else {
          this.setActiveMediaNative(mediaId, prepend);
        }
      }

      

      initSwipers() {
        const viewerEl = this.elements.viewer.querySelector('.swiper');
        if (typeof Swiper === 'undefined' || !viewerEl) return;

        if (this.elements.thumbnails) {
          const thumbsEl = this.elements.thumbnails.querySelector('.swiper');

          if (thumbsEl) {
            this.thumbsSwiper = new Swiper(thumbsEl, {
              slidesPerView: 3,
              spaceBetween: 15,
               
              watchSlidesProgress: true,
               
              grabCursor: true,
               freeMode: {
                enabled: true,
                sticky: false,
                momentumBounce: false,
              },
               
              observer: true,
              observeParents: true,
              breakpoints: {
                750: { slidesPerView: 4 , spaceBetween: 20 },
                990: { slidesPerView: 5.5 ,spaceBetween: 20 },
              },
              navigation: {
                prevEl: this.elements.thumbnails.querySelector('button[name="previous"]'),
                nextEl: this.elements.thumbnails.querySelector('button[name="next"]'),
              },
            });

             
            thumbsEl.addEventListener('dragstart', (event) => event.preventDefault());

            // Thumbnails are matched to slides by data-target, not by index: with "hide variants"
            // the thumbnail list holds every media item while the viewer only holds the current
            // variant's, so Swiper's index-based `thumbs` link would open the wrong slide.
            thumbsEl.addEventListener('click', (event) => {
              const thumbnail = event.target.closest('[data-target]');
              if (thumbnail) this.slideToMedia(thumbnail.dataset.target);
            });
          }
        }

        this.zoomButton = this.elements.viewer.querySelector('.product__gallery-zoom-button');

        this.viewerSwiper = new Swiper(viewerEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 400,
          grabCursor: true,
           
          watchOverflow: true,
          observer: true,
          observeParents: true,
          navigation: {
            prevEl: this.elements.viewer.querySelector('button[name="previous"]'),
            nextEl: this.elements.viewer.querySelector('button[name="next"]'),
          },
          pagination: {
            el: this.elements.viewer.querySelector('.swiper-pagination'),
            clickable: true,
          },
          on: {
             
            init: (swiper) => this.syncActiveSlide(false, swiper),
            slideChange: (swiper) => this.syncActiveSlide(true, swiper),
          },
        });
      }

       
      syncActiveSlide(userInitiated, swiper = this.viewerSwiper) {
        if (!swiper) return;

        const activeMedia = swiper.slides[swiper.activeIndex];
        if (!activeMedia) return;

        swiper.slides.forEach((slide) => slide.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        const activeThumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.elements.thumbnails
          ?.querySelectorAll('.swiper-slide-thumb-active')
          .forEach((thumbnail) => thumbnail.classList.remove('swiper-slide-thumb-active'));
        activeThumbnail?.classList.add('swiper-slide-thumb-active');
        if (userInitiated && activeThumbnail && this.thumbsSwiper) {
          const thumbIndex = this.thumbsSwiper.slides.indexOf(activeThumbnail);
          if (thumbIndex > -1) this.thumbsSwiper.slideTo(thumbIndex);
        }

        if (this.zoomButton && activeMedia.dataset.rawMediaId) {
          this.zoomButton.setAttribute('data-media-id', activeMedia.dataset.rawMediaId);
        }

        if (!userInitiated) return;

        this.playActiveMedia(activeMedia);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      slideToMedia(mediaId) {
        const index = this.viewerSwiper?.slides.findIndex((slide) => slide.dataset.mediaId === mediaId) ?? -1;
        if (index > -1) this.viewerSwiper.slideTo(index);
      }

      setActiveMediaSwiper(mediaId, prepend) {
        if (!this.viewerSwiper) return;

        // product-info.js has just added/removed <li> slides; resync Swiper's slide list first
        // so findSlide() never returns a slide that is no longer in the DOM.
        this.viewerSwiper.update();

        const findSlide = () =>
          this.viewerSwiper.slides.find((slide) => slide.dataset.mediaId === mediaId) ||
          this.viewerSwiper.slides[0];

        let activeMedia = findSlide();
        if (!activeMedia) return;

        if (prepend) {
          const list = activeMedia.parentElement;
          if (list && list.firstChild !== activeMedia) list.prepend(activeMedia);

          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

         
        this.thumbsSwiper?.update();
        this.viewerSwiper.update();

        activeMedia = findSlide();
        const index = this.viewerSwiper.slides.indexOf(activeMedia);
        if (index > -1) this.viewerSwiper.slideTo(index, 0);

        this.syncActiveSlide(false);
        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        const activeMediaRect = activeMedia.getBoundingClientRect();
         
        if (activeMediaRect.top > -0.5) return;
        window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
      }

       

      initNativeSlider() {
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMediaNative.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMediaNative(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this.useSwiperGallery) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }
  );
}
